import { BROWSER_INDUCED_ABORT_NAME } from '@/shared/utils/browserLifecycle';
import { CHUNK_LOAD_ERROR_PATTERNS } from '@/shared/utils/chunkErrorHandler';
import type { ErrorEvent, EventHint } from '@sentry/react';
import {
  browserProfilingIntegration,
  browserSessionIntegration,
  browserTracingIntegration,
  captureConsoleIntegration,
  extraErrorDataIntegration,
  getReplay,
  httpClientIntegration,
  init,
  replayCanvasIntegration,
  replayIntegration,
  zodErrorsIntegration,
} from '@sentry/react';

const dsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_ENVIRONMENT ?? 'development';
const version = import.meta.env.VITE_VERSION;
export const SENTRY_REPLAY_KEY = 'enableSentryReplay';

/**
 * A few notes:
 *
 * Replays w/canvas only seem to work if you do it on initialization (i.e.
 * you can't lazy load the integration via `.addIntegration()`).
 *
 * https://github.com/getsentry/sentry-javascript/issues/13754
 *
 * Because whether we do replays is dependent on a team preference, we store that
 * info in localStorage and then stop the recording if we find out the user's
 * preference
 *
 * So the logic goes like this:
 *
 * - When the app loads the very first time, see if there's a preference in localStorage.
 * - If there's not, start the recording. Then when we load team data and find out
 *   they have a preference to not record, we'll stop it.
 * - If there is a preference, we'll use that. And if we find out later they changed it,
 *   we'll update localstorage and stop the recording.
 *
 * The two main paths into the app (dashboard and the file) are the two routes
 * where we'll run this logic.
 */

const NETWORK_ERROR_PATTERNS = [
  /Failed to fetch/,
  /Load failed/,
  /NetworkError/,
  /Failed to load dynamically imported/,
];

// Matches on `name` as well as `message`, and reads both off any error-*like*
// object rather than requiring `instanceof Error`. Chrome reports a failed
// transport as a `DOMException` whose *name* carries the token (`NetworkError`)
// while its message is the generic "A network error occurred." (legacy
// `NETWORK_ERR`, code 19), so message-only matching let that variant through
// even though `/NetworkError/` is listed above for exactly this condition
// (INTERNAL-Q). Firefox instead puts the token in the message
// ("NetworkError when attempting to fetch resource."), which is why the gap went
// unnoticed. This mirrors `isNetworkFailure` in error.ts, which already treats
// `name === 'NetworkError'` as a transport failure.
function isNetworkError(hint: EventHint): boolean {
  const error = hint.originalException;
  if (typeof error === 'string') {
    return NETWORK_ERROR_PATTERNS.some((pattern) => pattern.test(error));
  }
  if (!error || typeof error !== 'object') return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  return NETWORK_ERROR_PATTERNS.some(
    (pattern) =>
      (typeof name === 'string' && pattern.test(name)) || (typeof message === 'string' && pattern.test(message))
  );
}

// Worker shells reject their init promise with this error name when the
// underlying onerror was attributed to a browser-induced teardown (Memory
// Saver / tab discard / freeze / offline). If that rejection bubbles up to
// React or an unhandled-rejection breadcrumb, drop the Sentry event here so
// it doesn't slip past the `worker.onerror` suppression.
function isBrowserInducedAbort(hint: EventHint): boolean {
  const error = hint.originalException;
  return error instanceof Error && error.name === BROWSER_INDUCED_ABORT_NAME;
}

// A promise rejecting with `AbortError` because *we* cancelled it. The canonical
// case (INTERNAL-6PJ): `resetAIAnalystInitialized` aborts the in-flight AI
// request on logout, and a moment later `authClient.logout` navigates the tab
// away. The stack on these is our own `abortController.abort()` call — that's
// where the browser constructs the DOMException, not where the promise rejected —
// so it identifies the canceller but never the consumer that lacked a catch.
// Cancelling is the intended behavior there and nothing is left broken, so the
// rejection is expected control flow rather than a defect.
//
// Every AI path that owns that shared signal already handles it
// (`AIAPIClient.sendRequest`, `AISession`'s tool-call loop, the agent-connection
// research pipeline, `useAIRequestToAPI` all catch `AbortError`), and the app
// makes this same call in three other places: `isNetworkFailure` in error.ts
// treats `name === 'AbortError'` as a transport failure, `fileLoadingAtom`
// swallows aborted file loads via `isAbortError`, and `isBrowserInducedAbort`
// above drops the worker-teardown variant.
//
// Deliberately scoped to the global unhandled-rejection handler, so an
// `AbortError` a call site chooses to report through `sendAnalyticsError` (with
// origin/from context attached) still comes through. `AbortSignal.timeout()`
// rejects with `TimeoutError`, not `AbortError`, so timeouts are unaffected.
// Reads `name` off any error-like object because a raw `DOMException` isn't
// reliably an `Error` instance (same caveat as `isStorageUnavailable`).
const UNHANDLED_REJECTION_MECHANISM = 'auto.browser.global_handlers.onunhandledrejection';
function isUnhandledAbortError(event: ErrorEvent, hint: EventHint): boolean {
  const exception = event.exception?.values?.[0];
  if (exception?.mechanism?.type !== UNHANDLED_REJECTION_MECHANISM) return false;
  if (exception.type === 'AbortError') return true;
  const error = hint.originalException;
  return !!error && typeof error === 'object' && (error as { name?: unknown }).name === 'AbortError';
}

// httpClientIntegration auto-captures every non-2xx fetch as an unhandled
// error, regardless of whether the application caught it. For /version.json
// that's noise: Multiplayer.checkVersion polls it every few seconds while
// waiting for the CDN to serve the new bundle after a deploy, and its own
// catch/retry already handles transient errors. Drop only that URL; keep the
// integration active for every other path where 4xx/5xx really is actionable.
function isVersionJsonHttpClientCapture(event: ErrorEvent): boolean {
  const mechanismType = event.exception?.values?.[0]?.mechanism?.type;
  if (mechanismType !== 'auto.http.client.fetch') return false;
  const requestUrl = event.request?.url ?? '';
  return /\/version\.json(\?|#|$)/.test(requestUrl);
}

// Same httpClientIntegration problem as `/version.json`, for the Mixpanel
// analytics proxy. mixpanel-browser batches events to our own /mixpanel-proxy
// on a timer; nothing in our code awaits that request, yet the integration
// reports each non-2xx as an *unhandled* error.
//
// A 5xx here is the proxy working as designed: quadratic-api's mixpanelProxy
// returns 502 when its own fetch to api.mixpanel.com throws and 504 when that
// call times out. It has to surface a 5xx rather than swallow the failure,
// because mixpanel-browser only retries on `httpStatusCode >= 500` (and 429) —
// it reschedules the flush with backoff and keeps the batch in its persisted
// queue, so a transient upstream Mixpanel outage costs us no events and needs
// no client-side handling.
//
// Only the statuses the SDK retries are dropped. Any other 4xx from this route
// is left reporting, since that would mean the proxy itself is misconfigured
// (an unlisted path 404ing, a CORS/body rejection) rather than third-party
// flake — a real bug we'd want to see.
function isRetriedMixpanelProxyCapture(event: ErrorEvent): boolean {
  const mechanismType = event.exception?.values?.[0]?.mechanism?.type;
  if (mechanismType !== 'auto.http.client.xhr' && mechanismType !== 'auto.http.client.fetch') return false;
  if (!/\/mixpanel-proxy\//.test(event.request?.url ?? '')) return false;

  const status = event.contexts?.response?.status_code;
  return typeof status === 'number' && (status >= 500 || status === 429);
}

// Third instance of the httpClientIntegration double-report, this time for the
// Google Sheets import endpoint. Its failures are all deliberate, user-facing
// responses from our own handler (teams.$uuid.google.sheets.download.POST.ts):
// 504 when the 60s fetch to the Google Sheets API is aborted ("The spreadsheet
// may be too large."), 401/404 for auth and missing sheets, 502 when Google
// itself fails.
//
// Every one of those is awaited by `GoogleSheetsImportDialog.doImport`, which
// catches it, shows the message in the dialog behind a "Try again" button, and
// reports it via `sendAnalyticsError` at `warning` with the team context
// attached. httpClientIntegration then emits a *second*, contextless event for
// the same failure at `error`/handled:no, overriding the level that call site
// deliberately chose. Drop the auto-capture; the app's own report is strictly
// more useful.
function isGoogleSheetsDownloadHttpClientCapture(event: ErrorEvent): boolean {
  const mechanismType = event.exception?.values?.[0]?.mechanism?.type;
  if (mechanismType !== 'auto.http.client.fetch') return false;
  return /\/google\/sheets\/download(\?|#|$)/.test(event.request?.url ?? '');
}

// Fourth instance of the httpClientIntegration double-report, this time for the
// core wasm asset fetched by `compileWasmModule` (wasmModule.ts). That code
// already handles a non-2xx: it means the hashed asset is gone, so it recovers
// with one guarded reload and rejects with `WasmAssetUnavailableError`. Both
// consumers then choose the severity deliberately — `quadraticCore` and
// `renderWebWorker` log at `warn` while a reload is in flight, explicitly "so it
// doesn't pollute the Sentry error feed", and escalate to `console.error` (which
// captureConsoleIntegration reports, with context) only when recovery already
// failed. The integration overrides that choice with a contextless
// `error`/handled:no event for the same response, and `preloadCore`'s caller
// swallows the rejection precisely because it isn't actionable.
//
// The reports are also dominated by bots rather than users: INTERNAL-6MX was a
// 503 to Baiduspider-render with no user attached — a CDN/proxy throttling a
// crawler on a ~27 MB asset, which is the condition wasmModule.ts already
// anticipates for truncated bodies.
//
// Scoped to the core wasm URL, so every other failing fetch keeps reporting. JS
// chunks need no entry here: they load via script/dynamic import rather than
// fetch, so the integration never sees them (CHUNK_LOAD_ERROR_PATTERNS in
// `ignoreErrors` covers that path).
function isCoreWasmAssetHttpClientCapture(event: ErrorEvent): boolean {
  const mechanismType = event.exception?.values?.[0]?.mechanism?.type;
  if (mechanismType !== 'auto.http.client.fetch') return false;
  return /quadratic_core_bg[^/]*\.wasm(\?|#|$)/.test(event.request?.url ?? '');
}

// Fifth instance of the httpClientIntegration double-report — except this
// request isn't ours at all. `/cdn-cgi/*` is served entirely by Cloudflare's
// edge for the zone and never reaches our origin; the string appears nowhere in
// this repo. The report is the Web Analytics beacon (the same
// `static.cloudflareinsights.com/beacon.min.js` the `denyUrls` entry below
// covers) POSTing its RUM sample to `/cdn-cgi/rum` on our own hostname and
// getting a 503 back from the edge. We can neither patch that script nor make
// Cloudflare's endpoint answer, and nothing of ours is broken when their
// telemetry drops a sample — INTERNAL-6Q7 is Baiduspider-render with no user
// attached, i.e. the edge shedding a crawler's beacon.
//
// `denyUrls` alone isn't enough here: it matches the *last* frame's filename
// (`_getLastValidUrl` in @sentry/core's eventFilters), so it only holds while
// the beacon's own frames sit at the top of the stack. httpClientIntegration
// synthesizes this event from an XHR response handler, where the stack is
// whatever happened to be running — it can just as easily bottom out in Sentry's
// wrapper or our bundle, leaving no cloudflareinsights URL to match. Filtering
// on the request URL is what makes the drop reliable.
//
// Scoped to the `/cdn-cgi/` prefix, so a failing request to any route we
// actually serve keeps reporting.
function isCloudflareEdgeHttpClientCapture(event: ErrorEvent): boolean {
  const mechanismType = event.exception?.values?.[0]?.mechanism?.type;
  if (mechanismType !== 'auto.http.client.xhr' && mechanismType !== 'auto.http.client.fetch') return false;
  return /\/cdn-cgi\//.test(event.request?.url ?? '');
}

function isHandledPythonChartCaptureFallback(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (exception?.mechanism?.type !== 'auto.core.capture_console') return false;
  const filename = exception.stacktrace?.frames?.find((frame) => frame.filename?.includes('pythonWebWorker'))?.filename;

  return (
    event.logger === 'console' &&
    filename !== undefined &&
    ['No content window', 'Plotly not available', 'No Plotly element found'].includes(exception.value ?? '')
  );
}

function isHandledEmojiMappingLoadFailure(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (exception?.mechanism?.type !== 'auto.core.capture_console') return false;
  if (event.logger !== 'console') return false;

  const args = event.extra?.arguments;
  return Array.isArray(args) && args[0] === '[Emojis] Failed to load emoji mapping:';
}
function isExpiredWorkosAuthCodeCapture(event: ErrorEvent): boolean {
  const value = event.exception?.values?.[0]?.value ?? event.message ?? '';
  return /^The code '[^']+' has expired\.$/.test(value);
}

// captureConsoleIntegration forwards every `console.error(...)` call. When the
// call carries no actionable content (an empty/whitespace string, or no args at
// all) it lands in Sentry as a "<unlabeled event>" with an empty message, no
// stack, and no filename — impossible to action and pure noise. Drop those.
function isEmptyConsoleErrorCapture(event: ErrorEvent): boolean {
  if (event.logger !== 'console') return false;
  const args = event.extra?.arguments;
  if (!Array.isArray(args)) return false;
  return args.every((arg) => arg === undefined || arg === null || (typeof arg === 'string' && arg.trim() === ''));
}

// PixiJS logs WebGL initialization failures (shader compile/link, program, or
// context creation) via console.error, which captureConsoleIntegration turns
// into Sentry errors. These originate from Pixi's own stock shaders — e.g. the
// MSDF bitmap-font shader in this report — failing on the user's GPU. That's a
// client driver/hardware/context condition (old or blocklisted GPU, exhausted
// WebGL contexts, driver reset/TDR), not an app bug we can fix in code, so it's
// unactionable noise in the error feed.
const PIXI_WEBGL_INIT_FAILURE_PATTERNS = [
  /Could not initialize shader/,
  /Could not create shader program/,
  /Could not create a? ?WebGL context/,
];
function isPixiWebGLInitFailure(event: ErrorEvent): boolean {
  if (event.logger !== 'console') return false;
  const value = event.exception?.values?.[0]?.value ?? event.message ?? '';
  const args = event.extra?.arguments;
  const firstArg = Array.isArray(args) && typeof args[0] === 'string' ? args[0] : '';
  return PIXI_WEBGL_INIT_FAILURE_PATTERNS.some((pattern) => pattern.test(value) || pattern.test(firstArg));
}

function isZoteroConnectorExtensionError(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  const value = exception?.value ?? event.message ?? '';
  const frames = exception?.stacktrace?.frames ?? [];
  const hasWebkitMaskedFrame = frames.some((frame) => frame.filename?.startsWith('webkit-masked-url:'));

  return value.startsWith('Zotero Connector: Failed to send message') && hasWebkitMaskedFrame;
}

// Injected third-party scripts that recurse until the stack is exhausted,
// throwing "RangeError: Maximum call stack size exceeded" out of whatever app
// code happened to be running. Two variants seen in production:
//
//   - The Google Search App in-app browser on iOS, together with Google
//     Translate's DOM rewriting (the `translated-ltr` class it adds to <html>),
//     injects sourceless scripts that recurse into the page and throw via the
//     global onerror handler. The only frame has filename "undefined" (a
//     `js_no_source`/`missing_source` error).
//   - A script that replaces a built-in and calls back through its own alias of
//     it, so the wrapper invokes itself forever — e.g. INTERNAL-5HX, a stack of
//     nothing but `Object.getOwnPropertyDescriptor [as _getOwnPropertyDescriptor]`
//     frames. Nothing in our source or in node_modules defines that alias, and
//     the recursion cycle contains no app frame at all; our code is only the
//     caller that tripped it (there, `aiAnalystOfflineChats.init`, which already
//     degrades to no-op offline chats via its `unavailable` flag).
//
// Neither can point at our code, and neither is fixable from it. `denyUrls`
// can't catch the second one: code evaluated via eval/new Function reports
// filename "<anonymous>" rather than an extension URL (the SDK's Chrome stack
// parser keeps that literal — it only normalizes `<anonymous>` *function*
// names), so it needs the same sourceless treatment as "undefined".
//
// Deliberately narrow: every frame must be sourceless. A genuine recursion bug
// in our own bundle symbolicates to app frames — and its repeated frames would
// be our own filenames — so it still reports.
const SOURCELESS_FRAME_FILENAMES = ['', 'undefined', '<anonymous>'];
function isSourcelessStackOverflow(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (exception?.type !== 'RangeError') return false;
  if (!/Maximum call stack size exceeded/.test(exception.value ?? '')) return false;

  const frames = exception.stacktrace?.frames ?? [];
  const hasRealFrame = frames.some((frame) => {
    const file = frame.filename ?? frame.abs_path ?? '';
    return !SOURCELESS_FRAME_FILENAMES.includes(file);
  });
  return !hasRealFrame;
}

// A `SyntaxError` arriving as a stackless unhandled rejection — the shape a
// dynamic `import()` produces when the script it fetched won't parse. V8 raises
// a parse failure with no JS call stack and puts no URL in the message, so the
// event has no frames at all and nothing identifying which script failed. It
// can't be attributed, and there is nothing to fix in code even when the script
// is ours: the bytes arrived corrupt.
//
// INTERNAL-1FX shows that end to end. The session is HeadlessChrome with no
// user and a 403 on the file it opened (a crawler), and it demonstrably
// received truncated bodies: the ~30 MB core wasm arrived as 15,659,538 of
// 29,712,127 bytes and failed to compile. A JS chunk cut off by the same
// transport ends mid-token, which is precisely this message. The page also runs
// GTM/Google Ads, which inject and import scripts of their own.
//
// Dropping it costs no coverage of the underlying condition: a truncated asset
// still reports through the path that can act on it, where wasmModule.ts
// surfaces the `CompileError` (carrying those byte counts) and runs its guarded
// recovery reload.
//
// Deliberately narrow — only the stackless rejection variant goes:
//  - A parse error in a statically-loaded bundle of ours arrives via `onerror`
//    with a filename, not as a rejection, so a bad deploy still reports.
//  - A `SyntaxError` thrown at runtime carries a stack, so it still reports.
//    `fetchFromApi`/`xhrFromApi` already handle their JSON ones, and user JS in
//    a code cell is caught in the worker by `javascriptFindSyntaxError`.
//  - Other wordings still report, notably "Unexpected token '<'" — an HTML page
//    served for a .js request, which is a CDN/deploy bug worth seeing.
//
// Message-matching in `ignoreErrors` can't express this: the bare text would
// also swallow the runtime and `onerror` variants above.
function isStacklessImportSyntaxError(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (exception?.mechanism?.type !== UNHANDLED_REJECTION_MECHANISM) return false;
  if (exception.type !== 'SyntaxError') return false;
  if (exception.value !== 'Invalid or unexpected token') return false;
  return (exception.stacktrace?.frames?.length ?? 0) === 0;
}

// When a tab exhausts its heap, Firefox raises a bare engine-level
// `Error: "out of memory"` (SpiderMonkey's JSMSG_OUT_OF_MEMORY) through
// `window.onerror`. Sentry receives it as a synthetic exception with
// `mechanism.type: 'internal'`, `synthetic: true`, and no stack trace at all,
// so it can never be attributed to a code path: the allocation that happened
// to fail is whatever ran next, not the code that consumed the memory.
//
// The same engine-level OOM also surfaces through a *rejected promise* whose
// reason is the bare string "out of memory" instead of an Error (INTERNAL-ND).
// The browser SDK routes primitive rejection reasons through
// `_eventFromRejectionWithPrimitive`, which prefixes the value, so that variant
// would otherwise slip past the anchored match below and report separately —
// equally stackless, and from the same feedback loop (that event carried 30
// identical `sentry.event` breadcrumbs inside 530ms).
//
// Capturing it also amplifies the condition it reports. Every capture
// allocates an event, appends a `sentry.event` breadcrumb that then rides
// along on the next event, and re-serializes that growing breadcrumb trail —
// and each of those allocations can itself trip another OOM. Dedupe doesn't
// break the loop because it only compares against the immediately preceding
// event, so interleaved captures slip through. That feedback loop is how one
// tab produced 224 identical stackless events in 11 seconds while starving
// the app of the memory it needed to render its own OOM error screen.
//
// Real OOMs still report through the paths that carry context: the
// `oom.detected` Mixpanel event (surface/source/stack excerpt, see
// oomAnalytics.ts) and `sendAnalyticsError`'s `captureException`, which tags
// `extra.outOfMemory` and has a stack. Both survive this filter — only the
// contextless engine-level variant is dropped.
const NON_ERROR_REJECTION_VALUE_PREFIX = 'Non-Error promise rejection captured with value: ';
// Firefox prefixes an uncaught error raised inside a Worker with this when it
// surfaces globally, so the worker-thread variant of the same engine OOM arrives
// as "uncaught exception: out of memory" (INTERNAL-6P6, thrown from
// pyright.worker.js) rather than the bare main-thread wording.
const WORKER_UNCAUGHT_EXCEPTION_PREFIX = 'uncaught exception: ';
const ENGINE_OOM_VALUE_PREFIXES = [NON_ERROR_REJECTION_VALUE_PREFIX, WORKER_UNCAUGHT_EXCEPTION_PREFIX];
function isStacklessEngineOutOfMemory(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  const rawValue = exception?.value ?? event.message ?? '';
  // Unwrap the known wrappers so every surface hits the same anchored test;
  // anchoring is what keeps a contextful message that merely mentions running
  // out of memory reporting.
  const value = ENGINE_OOM_VALUE_PREFIXES.reduce(
    (unwrapped, prefix) => (unwrapped.startsWith(prefix) ? unwrapped.slice(prefix.length) : unwrapped),
    rawValue
  );
  if (!/^\s*out of memory\s*$/i.test(value)) return false;

  // Anything routed through `sendAnalyticsError` is deliberate and contextful.
  if (event.extra?.outOfMemory === true) return false;

  // `synthetic` means the SDK had no thrown `Error` to work with and fabricated
  // one to recreate a stacktrace, so the frame it carries is whatever location
  // `onerror` reported — the code that happened to be running when an
  // allocation failed, not the code that consumed the memory. For a minified
  // worker bundle that is a column offset into a single line of third-party
  // code, which is no more actionable than the stackless main-thread variant,
  // so treat it the same rather than requiring a literally empty stack.
  if (exception?.mechanism?.synthetic === true) return true;

  const frames = exception?.stacktrace?.frames ?? [];
  const hasRealFrame = frames.some((frame) => {
    const file = frame.filename ?? frame.abs_path ?? '';
    return file !== '' && file !== 'undefined';
  });
  return !hasRealFrame;
}

// The follow-on traps after the core's wasm heap is already exhausted. Once an
// allocation aborts, the wasm instance is dead, and the *next* call into it
// fails while wasm-bindgen marshals its arguments in — so the deepest
// `quadratic_core.wasm.` frame is `__wbindgen_malloc` and the JS frames above it
// name whichever call happened to run next, not the code that consumed the
// memory. INTERNAL-6NS is exactly that: `setTransactionProvenance` (which passes
// a short provenance string) trapped seven seconds after the real OOM, which had
// already reported itself separately from `undo` with the
// `handle_alloc_error` / `RawVec` / `CodeRun::clone` frames that do name the
// consumer. Left unfiltered, one dead heap opens a fresh issue per subsequent
// call (undo, getAITransactions, receiveTransactionAck, getCellValue, ...), each
// with a misleading title and nothing to act on.
//
// This is what the `/__wbindgen_malloc/` entry in `ignoreErrors` above was meant
// to catch, but that can't work for this shape: the SDK matches `ignoreErrors`
// against `event.message` and the exception *value*, and the value here is just
// "unreachable" — the token only ever appears in a frame's `function`.
//
// Deliberately narrow, so the OOM report that can actually be triaged survives:
//  - the value must be the bare wasm trap wording, and
//  - the deepest wasm frame must be `__wbindgen_malloc`, i.e. the trap happened
//    marshalling arguments in rather than inside our Rust code. An OOM whose
//    stack bottoms out in `handle_alloc_error`/`RawVec` (INTERNAL-6NZ, in
//    `finalize_data_table`) keeps reporting, as does any non-allocator
//    `unreachable` panic (INTERNAL-6Q1's date arithmetic, which bottoms out in
//    `panic_with_hook`). `oom.detected` in Mixpanel — which carries the surface
//    and a stack excerpt — records these regardless.
const WASM_FRAME_PREFIX = 'quadratic_core.wasm.';
function isCascadedWasmMallocTrap(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  const value = exception?.value ?? event.message ?? '';
  // `error.message` is the bare wording; the raw stack's first line prefixes it.
  if (!/^\s*(RuntimeError:\s*)?unreachable\s*$/.test(value)) return false;

  // Sentry stores frames oldest-first, so the deepest (most recent) wasm frame
  // is the last one — the same frame `isOutOfMemoryError` (isOutOfMemoryError.ts)
  // inspects when it reads the top of `error.stack` to spot this cascade.
  const deepestWasmFrame = (exception?.stacktrace?.frames ?? [])
    .filter((frame) => frame.function?.startsWith(WASM_FRAME_PREFIX))
    .at(-1);
  return deepestWasmFrame?.function === `${WASM_FRAME_PREFIX}__wbindgen_malloc`;
}

// Dexie/IndexedDB failures that `sendAnalyticsError` already classified as "the
// browser won't give us local storage" (`isStorageUnavailable` in error.ts,
// which is what sets this `extra` flag). Every offline store degrades
// gracefully when this happens — reads return empty, writes are skipped — so
// the user keeps working without local persistence and there is nothing to fix
// in code.
//
// Only the two names that are purely browser/OS-level are dropped:
//   - UnknownError ("Internal error.") is the long-standing Chrome IndexedDB
//     failure (disk I/O, corrupt profile). Dexie ships its own retry for it and
//     logs "Dexie: Workaround for Chrome UnknownError on open()" — when that
//     retry is exhausted, as in INTERNAL-CE, there is no further recourse.
//   - DatabaseClosedError is the follow-on condition: once open() has failed,
//     every later read/write on that connection reports it, so a single bad
//     open would otherwise emit a burst of duplicates from every call site.
//
// QuotaExceededError and InvalidStateError deliberately keep reporting.
// Exhausting the quota can mean *we* are writing unbounded data, and an
// InvalidStateError can mean we misused the IndexedDB API — those are our bugs,
// not environment conditions.
const BROWSER_LEVEL_STORAGE_ERROR_NAMES = ['UnknownError', 'DatabaseClosedError'];
function isBrowserLevelStorageFailure(event: ErrorEvent): boolean {
  if (event.extra?.storageUnavailable !== true) return false;
  const exceptionType = event.exception?.values?.[0]?.type ?? '';
  return BROWSER_LEVEL_STORAGE_ERROR_NAMES.includes(exceptionType);
}

// An unhandled rejection whose value is a bare `{ code, message }` object
// instead of an Error (INTERNAL-5DA, on /login-result). There is nothing in the
// event to act on: the browser SDK's `onunhandledrejection` handler builds these
// via `eventFromPlainObject` with no `syntheticException`, so the event has no
// frames at all — no message text survives either, and the issue title renders
// as `<unknown>`. All that reaches Sentry is the SDK's sorted key list.
//
// It also doesn't come from us. Every `throw`/`reject` in quadratic-client
// constructs an Error (or the ApiError / SchemaConfigurationError subclasses),
// and authkit-js — the only auth SDK on the login callback route — throws Error
// subclasses exclusively and `.catch()`es its one floating promise (the
// auto-refresh timer). A bare `{ code, message }` rejected on a login page with
// no stack is the shape injected content scripts produce: EIP-1193 wallet
// providers reject with exactly `{ code, message }`, and password managers hook
// login pages. `denyUrls` can't filter it, because there are no frames to match
// a URL against.
//
// Narrow on purpose: only unhandled rejections, and only this exact key set. If
// we ever take on a dependency that rejects plain `{ code, message }` objects
// for real failures, revisit this — the SDK keeps the rejected object on
// `event.extra.__serialized__`, which is where to read the actual code/message
// off a live event.
const NON_ERROR_CODE_MESSAGE_REJECTION_VALUE = 'Object captured as promise rejection with keys: code, message';
function isNonErrorCodeMessageRejection(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (exception?.type !== 'UnhandledRejection') return false;
  return exception.value === NON_ERROR_CODE_MESSAGE_REJECTION_VALUE;
}

export const initSentry = () => {
  let useReplay = true;
  if (localStorage.getItem(SENTRY_REPLAY_KEY) === 'false') {
    useReplay = false;
  }

  try {
    if (dsn && dsn !== 'none') {
      init({
        dsn,
        environment,
        release: `quadratic@${version}`,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        ignoreErrors: [
          /AuthKit: Failed to send logout request/,
          /Load failed \(authenticate\.quadratichq\.com\)/,
          /No user found after signin redirect/,
          // Only matches when the token is in the message/exception value (the
          // iOS wasm worker init failures this was added for). The post-OOM
          // cascade traps, whose value is just "unreachable" and which carry the
          // token in a frame instead, need `isCascadedWasmMallocTrap` below.
          /__wbindgen_malloc/,
          /InstantiationService has been disposed/,
          /Got bad scroll event/,
          /Data lost due to missing file/,
          /recursive use of an object detected/,
          // WebKit's NotAllowedError for a denied clipboard/permission request.
          /The request is not allowed by the user agent or the platform in the current context/,
          // Blink's wording for the same condition, which the pattern above
          // doesn't cover: the user (or an enterprise policy) blocked clipboard
          // access for the site, so `navigator.clipboard.read()/write()` rejects.
          // We can't grant ourselves the permission, so there's nothing to fix in
          // code — clipboard.tsx already catches it, shows a snackbar, and records
          // a `[clipboard].error` Mixpanel event, which is where this belongs.
          //
          // Matched on "permission denied" specifically rather than on the error
          // name or the `origin: clipboard` tag, because other clipboard
          // NotAllowedErrors have been real bugs: "Document is not focused." was
          // INTERNAL-6ND, where the grid hijacked Ctrl+C on the dashboard. That
          // variant must keep reporting.
          /on 'Clipboard': (Read|Write) permission denied/,
          /Failed to execute 'removeChild' on 'Node'/,
          /Failed to execute 'insertBefore' on 'Node'/,
          /Couldn't exchange code/,
          /^Canceled$/,
          // Our own import guard rails rejecting a file the user picked: core's
          // MB / cell-count caps (quadratic-core lib.rs + operations/import.rs),
          // which reach the client as `result.error` and get re-thrown by the
          // import call sites. Those messages share this shape, and the phrase
          // appears nowhere else.
          //
          // Hitting a documented limit is the product working as designed, not a
          // defect — the call sites catch it, show the message in a snackbar, and
          // record a Mixpanel event, which is the right place to watch how often
          // users bump into the caps. Sentry started collecting these when the
          // caps shipped in 0.26.10.
          //
          // The client-side pre-flight cap (fileSizeLimits.ts) no longer relies
          // on this pattern: every call site (useFileImport, importFilesToSheet,
          // AIUserMessageForm) handles that rejection with a snackbar plus a
          // Mixpanel event, without reporting it to Sentry at all.
          //
          // Deliberately narrow: only the "too large" rejections are dropped.
          // Other import failures (corrupt/mislabeled files, parser errors) keep
          // reporting, since a spike there can mean the importer itself
          // regressed and started refusing valid files.
          /is too large to import \(/,
          // Multiplayer.checkVersion's catch-block console.warn re-captured by
          // captureConsoleIntegration if levels ever expands to include 'warn'.
          /\[multiplayer\.ts\] checkVersion: Failed to fetch \/version\.json/,
          // A script the X/Twitter iOS in-app browser injects into the page,
          // throwing out of its own `init` because it references an undeclared
          // `currentInset` (safe-area inset handling). Reported via the global
          // onerror handler with `browser: Twitter 12.11`, a `t.co` referrer, and
          // UA "Twitter for iPhone" — the WebView opens tapped links in a
          // WKWebView and runs its `WKUserScript` in our document's scope.
          //
          // `currentInset` appears nowhere in quadratic-client or in any
          // dependency, so we can neither declare it for them nor fix their
          // bootstrap; nothing in the app is broken by it. `denyUrls` can't catch
          // it either: because the injected script runs as part of the document,
          // every frame carries our own document URL (`/file/:uuid`) rather than
          // an extension URL, with line numbers (486, 503) well past the end of
          // our HTML. Same caveat as `isSourcelessStackOverflow` above.
          //
          // Anchored on the identifier, which is what makes this safe — we would
          // never reference that name, so this can't mask one of our own
          // ReferenceErrors. Both engines' wordings are covered (WebKit's "Can't
          // find variable" is the one seen in production; the Android in-app
          // browser is Chromium, which words it "is not defined").
          /Can't find variable: currentInset|currentInset is not defined/,
          // A Safari Web Extension messaging its own background script from a
          // content script running in our document. WebKit rejects
          // `runtime.sendMessage()` with this exact wording when it can't
          // resolve the tab the message came from, which is what happens when
          // the tab is navigating or being torn down. INTERNAL-6Q3 is exactly
          // that: three rejections inside three seconds on Mobile Safari 26.6 /
          // iOS 18.7, with a `/login` referrer, i.e. as the page moved from
          // /login to /.
          //
          // `runtime.sendMessage` is a WebExtension API. It appears nowhere in
          // quadratic-client or in any dependency we ship (the sole match in
          // node_modules is the Electron binary, which never reaches a
          // browser), so the call can only come from the user's own extension.
          // Nothing in the app is broken by it — no user was even attached to
          // the event — and there is nothing to fix in code.
          //
          // `denyUrls` can't catch it: the rejection arrives with no stacktrace
          // at all, so there are no frames to match against
          // `safari-extension://` (same caveat as `isSourcelessStackOverflow`
          // above, for a different reason).
          //
          // Anchored on the API name, which is what makes this safe — we never
          // call it, so this can't mask one of our own errors.
          /Invalid call to runtime\.sendMessage\(\)/,
          // Chunk loading errors are handled by chunkErrorHandler.ts which automatically reloads the page
          ...CHUNK_LOAD_ERROR_PATTERNS,
        ],
        denyUrls: [
          /^chrome-extension:\/\//,
          /^moz-extension:\/\//,
          /^safari-extension:\/\//,
          // Cloudflare Web Analytics' beacon, injected at the edge by the zone —
          // it appears nowhere in our source, our HTML, or our dependencies, so
          // we can neither patch nor version-pin it. The bundled web-vitals code
          // calls `entries.at(-1)`, and `Array.prototype.at` only exists from
          // Chrome 92; INTERNAL-6KK is Chrome 79 (2019), where that throws
          // inside the beacon's own INP/FCP attribution. Every frame in the
          // report is beacon.min.js, no app frame is involved, and nothing of
          // ours is broken when their telemetry fails on an ancient browser.
          //
          // `denyUrls` rather than `ignoreErrors` because the frames do carry a
          // real URL here, so the drop can be scoped to that one third-party
          // host instead of to a message shape (`.at is not a function`) that
          // our own bundle could produce on those same old browsers.
          /static\.cloudflareinsights\.com/,
        ],
        integrations: [
          browserProfilingIntegration(),
          browserSessionIntegration(),
          browserTracingIntegration(),
          captureConsoleIntegration({ levels: ['error'] }),
          extraErrorDataIntegration(),
          httpClientIntegration(),
          zodErrorsIntegration(),
          ...(useReplay
            ? [
                replayIntegration({
                  maskAllText: false,
                  maskAllInputs: false,
                  blockAllMedia: false,
                  networkDetailDenyUrls: ['/iframe-indexeddb'],
                  // Sentry classifies a click as "dead" (and 3+ repeats as a
                  // "rage click") when it produces no DOM mutation or page
                  // scroll within the timeout. Controls that respond only by
                  // scrolling an internal container — e.g. the sheet-bar
                  // navigation chevrons, which repeatedly nudge scrollLeft — get
                  // flagged as false-positive rage clicks even though clicking
                  // them repeatedly is normal. Opt those elements out with the
                  // `data-slow-click-ignore` marker.
                  //
                  // This option is the ONLY client-side lever over Dead/Rage
                  // Click issues. The SDK never captures them as error events —
                  // it just writes a `ui.slowClickDetected` breadcrumb (with a
                  // `clickCount`) into the replay stream, and Sentry's backend
                  // synthesizes the issue from that. So `ignoreErrors` and the
                  // `beforeSend` filters below can't see or drop them; adding a
                  // pattern there for a "Rage Click" issue is dead code.
                  //
                  // Detection only considers A / BUTTON / INPUT[type=submit|button]
                  // (anchors with `download` or a non-`_self` target are already
                  // exempt), so triage starts by opening the issue's replay and
                  // reading the selector off that breadcrumb — the issue event
                  // itself carries no exception, culprit, or stack to work from.
                  //
                  // On a `/file/:uuid` report, don't assume a grid repaint counts
                  // as "no response": rrweb's mutation set includes
                  // CanvasMutation, and `PixiApp` sets `preserveDrawingBuffer:
                  // true` specifically so these snapshots capture the grid, so a
                  // canvas-only response does register — but only when the
                  // rendered image actually changes (the canvas worker replies
                  // without `base64` for an unchanged frame, and the manager then
                  // emits nothing). So a dead click there means a control that
                  // changed neither the DOM nor the rendered grid.
                  slowClickIgnoreSelectors: ['[data-slow-click-ignore]'],
                }),
                replayCanvasIntegration({ enableManualSnapshot: false, quality: 'high' }),
              ]
            : []),
        ],
        // Error events only. The SDK routes transaction events to
        // `beforeSendTransaction` instead (see `@sentry/core` client.js — the
        // `beforeSend` branch is guarded by `!isTransactionEvent`), so Sentry's
        // backend-synthesized *performance* issues are unreachable from here:
        // "Large HTTP payload" (INTERNAL-24M), N+1 API calls, slow resource, etc.
        // are derived from a transaction's span data, arrive with `level: info`
        // and no exception at all, and no `ignoreErrors` pattern or filter below
        // can match them — adding one for a performance issue is dead code. The
        // only client-side levers for those are `beforeSendTransaction` and
        // `ignoreSpans`, both of which trade away the latency telemetry for the
        // route or request in question, so prefer shrinking the payload (or
        // tuning the detector threshold in Sentry's project settings) instead.
        beforeSend(event: ErrorEvent, hint: EventHint) {
          if (isNetworkError(hint)) {
            return null;
          }
          if (isBrowserInducedAbort(hint)) {
            return null;
          }
          if (isUnhandledAbortError(event, hint)) {
            return null;
          }
          if (isVersionJsonHttpClientCapture(event)) {
            return null;
          }
          if (isRetriedMixpanelProxyCapture(event)) {
            return null;
          }
          if (isGoogleSheetsDownloadHttpClientCapture(event)) {
            return null;
          }
          if (isCoreWasmAssetHttpClientCapture(event)) {
            return null;
          }
          if (isCloudflareEdgeHttpClientCapture(event)) {
            return null;
          }
          if (isHandledPythonChartCaptureFallback(event)) {
            return null;
          }
          if (isHandledEmojiMappingLoadFailure(event)) {
            return null;
          }
          if (isExpiredWorkosAuthCodeCapture(event)) {
            return null;
          }
          if (isEmptyConsoleErrorCapture(event)) {
            return null;
          }
          if (isZoteroConnectorExtensionError(event)) {
            return null;
          }
          if (isPixiWebGLInitFailure(event)) {
            return null;
          }
          if (isSourcelessStackOverflow(event)) {
            return null;
          }
          if (isStacklessImportSyntaxError(event)) {
            return null;
          }
          if (isStacklessEngineOutOfMemory(event)) {
            return null;
          }
          if (isCascadedWasmMallocTrap(event)) {
            return null;
          }
          if (isBrowserLevelStorageFailure(event)) {
            return null;
          }
          if (isNonErrorCodeMessageRejection(event)) {
            return null;
          }
          return event;
        },
        profilesSampleRate: 1.0,
        tracesSampleRate: 1.0,
      });
    }
  } catch (error) {
    console.error('Error initializing Sentry', error);
  }
};

const stopRecording = () => {
  const replay = getReplay();
  if (replay) {
    replay.stop();
  }
};

export const handleSentryReplays = (useReplayPreferenceFromServer: boolean) => {
  // Get the current preference on the client
  const currentPreference = localStorage.getItem(SENTRY_REPLAY_KEY);

  // If it's never been set, we'll set it and stop the recording if it doesn't match the server preference
  if (currentPreference === null) {
    localStorage.setItem(SENTRY_REPLAY_KEY, useReplayPreferenceFromServer ? 'true' : 'false');
    if (useReplayPreferenceFromServer === false) {
      stopRecording();
    }
    return;
  }

  // If it has been set, we'll use that
  const useReplayFromClient = currentPreference === 'true';

  // If the preference is different from the server, we'll set it
  if (useReplayFromClient !== useReplayPreferenceFromServer) {
    localStorage.setItem(SENTRY_REPLAY_KEY, useReplayPreferenceFromServer ? 'true' : 'false');

    // And if they changed it to not record, we'll stop it
    if (useReplayPreferenceFromServer === false) {
      stopRecording();
    }
    return;
  }

  // Otherwise, do nothing
};
